import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState, useMemo } from "react";
import { toast } from "sonner";
import { Search, Loader2, ChevronRight, Ban, ShieldCheck, Users, Filter } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AdminNav } from "@/components/admin/AdminNav";
import { AdminOnly } from "@/components/admin/AdminOnly";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import {
  adminListUsers,
  adminBanUser,
  adminUnbanUser,
  adminResetDevices,
  type AdminUserRow,
} from "@/lib/admin-security.functions";

export const Route = createFileRoute("/_authenticated/admin/users")({
  head: () => ({ meta: [{ title: "Users — Admin", }, { name: "robots", content: "noindex" }] }),
  component: () => (
    <AdminOnly>
      <AdminNav />
      <UsersPage />
    </AdminOnly>
  ),
});

function UsersPage() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"" | "active" | "suspended" | "banned">("");
  const qc = useQueryClient();
  const runList = useServerFn(adminListUsers);
  const runBan = useServerFn(adminBanUser);
  const runUnban = useServerFn(adminUnbanUser);
  const runReset = useServerFn(adminResetDevices);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-users", search, status],
    queryFn: () => runList({ data: { search, status, limit: 200, offset: 0 } }),
  });

  const users = useMemo(() => data?.users ?? [], [data]);

  const ban = useMutation({
    mutationFn: async (u: AdminUserRow) => {
      const reason = window.prompt(`Ban ${u.email}?\nEnter reason:`)?.trim();
      if (!reason) return null;
      return runBan({ data: { userId: u.id, reason } });
    },
    onSuccess: (r) => { if (r) { toast.success("User banned"); qc.invalidateQueries({ queryKey: ["admin-users"] }); } },
    onError: (e: Error) => toast.error(e.message),
  });

  const unban = useMutation({
    mutationFn: (u: AdminUserRow) => runUnban({ data: { userId: u.id, resetDevices: true } }),
    onSuccess: () => { toast.success("User unbanned"); qc.invalidateQueries({ queryKey: ["admin-users"] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  const resetDevices = useMutation({
    mutationFn: (u: AdminUserRow) => runReset({ data: { userId: u.id } }),
    onSuccess: (r) => { toast.success(`Reset ${r.deactivated} device(s)`); qc.invalidateQueries({ queryKey: ["admin-users"] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-5">
      <AdminPageHeader
        icon={Users}
        title="Users"
        subtitle={`${users.length} shown${search || status ? " (filtered)" : ""}. Click a row for full details.`}
      />


      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by email or name…"
            className="h-10 ps-9"
          />
        </div>
        <div className="inline-flex items-center gap-1 rounded-full border bg-muted/40 p-1">
          <Filter className="ms-2 h-3.5 w-3.5 text-muted-foreground" />
          {(["", "active", "suspended", "banned"] as const).map((s) => (
            <button
              key={s || "all"}
              type="button"
              onClick={() => setStatus(s)}
              className={
                "rounded-full px-3 py-1 text-xs font-medium transition " +
                (status === s ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground")
              }
            >
              {s ? s[0].toUpperCase() + s.slice(1) : "All"}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border bg-card">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
            <Loader2 className="me-2 h-4 w-4 animate-spin" /> Loading users…
          </div>
        ) : users.length === 0 ? (
          <div className="py-16 text-center text-sm text-muted-foreground">No users match.</div>
        ) : (
          <div className="w-full overflow-x-auto [-webkit-overflow-scrolling:touch]">
          <table className="w-full min-w-[680px] text-sm">
            <thead className="bg-muted/40 text-start text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Plan</th>
                <th className="px-4 py-3 text-center">Devices</th>
                <th className="px-4 py-3 text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t transition-colors hover:bg-muted/30">
                  <td className="px-4 py-3">
                    <Link to="/admin/users/$id" params={{ id: u.id }} className="group inline-flex items-center gap-2">
                      <div>
                        <div className="font-medium">{u.full_name ?? "—"}</div>
                        <div className="text-xs text-muted-foreground">{u.email}</div>
                      </div>
                      <ChevronRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-xs">{u.role}</div>
                    {u.admin_role !== "user" && (
                      <div className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
                        {u.admin_role}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <StatusPill status={u.account_status} />
                  </td>
                  <td className="px-4 py-3 text-xs">{u.subscription_tier}</td>
                  <td className="px-4 py-3 text-center font-mono text-xs tabular-nums">
                    {u.active_devices}/{u.max_devices}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        disabled={resetDevices.isPending || u.active_devices === 0}
                        onClick={() => resetDevices.mutate(u)}
                      >
                        Reset devices
                      </Button>
                      {u.is_banned ? (
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => unban.mutate(u)} disabled={unban.isPending}>
                          <ShieldCheck className="me-1 h-3 w-3" /> Unban
                        </Button>
                      ) : (
                        <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={() => ban.mutate(u)} disabled={ban.isPending}>
                          <Ban className="me-1 h-3 w-3" /> Ban
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: "active" | "suspended" | "banned" }) {
  const cls =
    status === "active"
      ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
      : status === "suspended"
      ? "bg-amber-500/10 text-amber-600 dark:text-amber-400"
      : "bg-red-500/10 text-red-600 dark:text-red-400";
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${cls}`}>{status}</span>;
}
